import {
  type AffiliateSummary,
  type AuthTelegramRequest,
  type AuthTelegramResponse,
  AuthTelegramResponseSchema,
  type ReasonCode,
  type TraderId,
} from '../../api';
import type { TelegramPort } from '../telegram/telegram-port';

import { ApiError, type ApiClient, toApiError } from './api-client';

/** S2 R18: the proactive renewal is due this long before `expiresAt`. */
const RENEW_BEFORE_EXPIRY_MS = 60_000;

/**
 * Proposal, to be validated (P1): the proactive renewal never fires sooner than this after the
 * previous exchange. It bounds a loop caused by a device clock ahead of the server, or an
 * `expiresAt` already past: at worst one exchange a minute. The 401 `TOKEN_EXPIRED` renewal is the
 * backstop when the timer is wrong.
 */
const MIN_RENEWAL_DELAY_MS = 60_000;

/**
 * What the app may know about the session. The access token is not in it: it stays in the closure
 * of {@link createSession} and leaves only through `Session.accessToken()` (S2 R14, I9).
 */
export type SessionState =
  | { readonly status: 'opening' }
  | {
      readonly status: 'ready';
      /** Opaque id of the trader, from the opening exchange. */
      readonly traderId: TraderId;
      /** True when the opening exchange created the trader (S3 R5, provisional). */
      readonly isNew: boolean;
      /** The affiliate locked on the first visit, by display name only (I8), or `null`. */
      readonly affiliate: AffiliateSummary | null;
    }
  | {
      readonly status: 'failed';
      /** Stable code shown with `reasonMessage`; never a raw message (I12). */
      readonly reasonCode: ReasonCode;
      /** True when pressing "Try again" can help (spec §4: 429, 503, 5xx, network failure). */
      readonly retryable: boolean;
    };

/** Failures a new attempt can fix; the others need the trader to reopen FXMate (spec §4). */
const RETRYABLE_REASON_CODES: ReadonlySet<ReasonCode> = new Set<ReasonCode>([
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
  'INTERNAL_ERROR',
]);

/** What {@link createSession} needs. */
export interface SessionOptions {
  /** Only `initDataRaw` is used, read at each exchange (S2 R14). */
  readonly telegram: Pick<TelegramPort, 'initDataRaw'>;
  /**
   * Client used for the exchange only. It must have no token getter and no renewal, so the
   * exchange carries no Bearer and can never trigger a renewal of itself (S2 R14, R18).
   */
  readonly exchangeApi: Pick<ApiClient, 'post'>;
}

/** The session service: obtains, holds and renews the access token, and tells the app where it stands. */
export interface Session {
  /** Current state; the same object until it changes, as `useSyncExternalStore` requires. */
  readonly getState: () => SessionState;
  /** Calls `listener` after each state change; returns the function that removes it. */
  readonly subscribe: (listener: () => void) => () => void;
  /**
   * Sends the opening exchange once (safe to call twice, as React StrictMode does) and, when the
   * session is ready, arms the proactive renewal.
   */
  readonly start: () => void;
  /** Cancels the proactive renewal. An exchange that finishes later arms none (unmount, AC22). */
  readonly stop: () => void;
  /** Sends the exchange again after a failure that retrying can fix; ignored otherwise. */
  readonly retry: () => void;
  /** The current access token, for the API client only. Never put it in state or the DOM. */
  readonly accessToken: () => string | undefined;
  /**
   * The client's `renewToken`: renews after a 401 `TOKEN_EXPIRED`. Callers that arrive together
   * share one exchange (single-flight, AC36); a caller whose token was already replaced returns at
   * once. Rejects with the `ApiError` of the exchange, or at once when the session has failed.
   */
  readonly renew: (staleToken: string | undefined) => Promise<void>;
  /** The client's `onSessionEnded`: a 401 no renewal can fix ends a ready session (spec §4). */
  readonly end: (error: ApiError) => void;
}

/**
 * Creates the session: the exchange of the Telegram `initData` for an access token, held in
 * memory only, renewed before it expires (master X1; S2 R14, R18, AC24).
 *
 * The token is opaque to the client: its format is the API's choice (S2 R3), and its lifetime
 * comes only from `expiresAt`.
 */
export function createSession(options: SessionOptions): Session {
  let state: SessionState = { status: 'opening' };
  let token: string | undefined;
  let expiresAt: string | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  let inFlight: Promise<ApiError | null> | null = null;
  const listeners = new Set<() => void>();

  function setState(next: SessionState): void {
    state = next;
    for (const listener of [...listeners]) listener();
  }

  function clearTimer(): void {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  }

  function scheduleRenewal(): void {
    clearTimer();
    // An answer that arrives after stop() (unmount) must not arm a timer.
    if (!running || expiresAt === undefined) return;
    const untilExpiry = Date.parse(expiresAt) - Date.now();
    const delay = Math.max(untilExpiry - RENEW_BEFORE_EXPIRY_MS, MIN_RENEWAL_DELAY_MS);
    timer = setTimeout(() => {
      timer = undefined;
      // A terminal failure is already in the state; a transient one is retried by the next 401.
      void runExchange();
    }, delay);
  }

  function requestToken(): Promise<AuthTelegramResponse> {
    // R14, AC37: read at each exchange, used for this one call and never kept.
    const initData = options.telegram.initDataRaw();
    // AC19: outside Telegram there is nothing to send; no request is made.
    if (initData === undefined) return Promise.reject(new ApiError(0, 'INIT_DATA_MISSING'));
    const body: AuthTelegramRequest = { initData };
    return options.exchangeApi.post(
      '/auth/telegram',
      { guard: { kind: 'public' }, body },
      AuthTelegramResponseSchema,
    );
  }

  function accept(response: AuthTelegramResponse): void {
    if (state.status === 'ready' && state.traderId !== response.traderId) {
      // X7, P7: a token for another trader is never adopted in the middle of a session.
      throw new ApiError(0, 'UNAUTHENTICATED');
    }
    token = response.accessToken;
    expiresAt = response.expiresAt;
    // P4: a renewal changes the token only; isNew and affiliate describe the opening.
    if (state.status !== 'ready') {
      setState({
        status: 'ready',
        traderId: response.traderId,
        isNew: response.isNew,
        affiliate: response.affiliate,
      });
    }
    scheduleRenewal();
  }

  function endWith(reasonCode: ReasonCode, retryable: boolean): void {
    token = undefined;
    expiresAt = undefined;
    clearTimer();
    setState({ status: 'failed', reasonCode, retryable });
  }

  function recordFailure(error: ApiError): ApiError {
    const retryable = RETRYABLE_REASON_CODES.has(error.reasonCode);
    // A hiccup while renewing keeps the session: the current token is valid until it expires and
    // the next 401 TOKEN_EXPIRED renews again. Anything else ends it (R18: INIT_DATA_EXPIRED).
    if (state.status === 'ready' && retryable) return error;
    endWith(error.reasonCode, retryable);
    return error;
  }

  async function performExchange(): Promise<ApiError | null> {
    try {
      accept(await requestToken());
      return null;
    } catch (error) {
      return recordFailure(toApiError(error));
    }
  }

  /**
   * One exchange at a time: callers that arrive while one is running share it (AC36). It resolves
   * with the failure instead of rejecting, because the failure is already in the state.
   */
  function runExchange(): Promise<ApiError | null> {
    inFlight ??= performExchange().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  function start(): void {
    running = true;
    if (state.status === 'opening') void runExchange();
    else if (state.status === 'ready') scheduleRenewal();
  }

  function stop(): void {
    running = false;
    clearTimer();
  }

  function retry(): void {
    if (state.status !== 'failed' || !state.retryable) return;
    setState({ status: 'opening' });
    void runExchange();
  }

  async function renew(staleToken: string | undefined): Promise<void> {
    // After a refused renewal every later 401 fails at once instead of exchanging again: no storm.
    if (state.status === 'failed') throw new ApiError(0, state.reasonCode);
    // Another request already renewed: the caller only has to replay with the new token.
    if (token !== undefined && token !== staleToken) return;
    const failure = await runExchange();
    if (failure !== null) throw failure;
  }

  function end(error: ApiError): void {
    if (state.status === 'ready') endWith(error.reasonCode, false);
  }

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    stop,
    retry,
    accessToken: () => token,
    renew,
    end,
  };
}
